/**
 * useMentalHealthIndex Hook
 * =========================
 * Manages Mental Health Interaction Index state for a visit.
 * - Generate via POST (LLM primary, VADER fallback on backend)
 * - Validate (with optional edits) via PUT /validate
 * - Discard via PUT /discard
 * - Status: draft | validated | discarded
 */
import { useState, useCallback, useEffect } from 'react';

// ──────────────────────────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────────────────────────

export interface MHIFactor {
  score: number;           // 0–100
  signals: string[];       // contributing phrases
  flags: string[];         // high-risk verbatim phrases (risk_review only)
  label: string;           // None|Mild|Moderate|Significant|Severe
}

export interface MHIPayload {
  emotional_distress: MHIFactor;
  affect_observed: MHIFactor;
  symptom_burden: MHIFactor;
  risk_review: MHIFactor;
  overall_score: number;
  overall_label: string;
  key_drivers: string[];
  confidence: 'low' | 'medium' | 'high';
  method: 'llm' | 'vader' | 'rules' | 'none';
}

export interface MHIRecord {
  mhi_id: number;
  visit_id: number;
  session_id: string | null;
  payload: MHIPayload;
  status: 'draft' | 'validated' | 'discarded';
  generated_at: string;
  validated_at: string | null;
  updated_at: string;
}

// ──────────────────────────────────────────────────────────────────────────────
// HOOK
// ──────────────────────────────────────────────────────────────────────────────

export function useMentalHealthIndex(visitId: number | null, sessionId?: string | null) {
  const [record, setRecord] = useState<MHIRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch stored index when visitId changes
  useEffect(() => {
    if (!visitId) {
      setRecord(null);
      setError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/patient-summary/visits/${visitId}/mental-health-index`);
        if (res.status === 404) {
          if (!cancelled) { setRecord(null); setError(null); }
          return;
        }
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `Error ${res.status}`);
        }
        const data: MHIRecord = await res.json();
        if (!cancelled) setRecord(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Unknown error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [visitId]);

  const generate = useCallback(async () => {
    if (!visitId) return;
    setGenerating(true);
    setError(null);
    try {
      const params = sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : '';
      const res = await fetch(
        `/api/patient-summary/visits/${visitId}/mental-health-index${params}`,
        { method: 'POST' }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Generation failed: ${res.status}`);
      }
      setRecord(await res.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setGenerating(false);
    }
  }, [visitId, sessionId]);

  const validate = useCallback(async (editedPayload?: Partial<MHIPayload>) => {
    if (!visitId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/patient-summary/visits/${visitId}/mental-health-index/validate`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ payload: editedPayload ?? null }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Validate failed: ${res.status}`);
      }
      setRecord(await res.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [visitId]);

  const discard = useCallback(async () => {
    if (!visitId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/patient-summary/visits/${visitId}/mental-health-index/discard`,
        { method: 'PUT' }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Discard failed: ${res.status}`);
      }
      setRecord(await res.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [visitId]);

  return { record, loading, generating, error, generate, validate, discard };
}
